import { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { X, Check } from 'lucide-react'
import { Button } from './Button'

const CONTAINER = 320
const CROP_D = 264

export function ImageCropper({ src, onCrop, onCancel }) {
  const imgRef = useRef()
  const containerRef = useRef()
  const dragging = useRef(false)
  const dragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 })

  const [natural, setNatural] = useState({ w: 1, h: 1 })
  const [loaded, setLoaded] = useState(false)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)

  const fitScale = loaded
    ? Math.max(CONTAINER / natural.w, CONTAINER / natural.h)
    : 1
  const dw = natural.w * fitScale * zoom
  const dh = natural.h * fitScale * zoom

  function clamp(ox, oy, w, h) {
    const mx = Math.max(0, (w - CROP_D) / 2)
    const my = Math.max(0, (h - CROP_D) / 2)
    return {
      x: Math.min(mx, Math.max(-mx, ox)),
      y: Math.min(my, Math.max(-my, oy)),
    }
  }

  function onPointerDown(e) {
    e.preventDefault()
    dragging.current = true
    dragStart.current = { mx: e.clientX, my: e.clientY, ox: offset.x, oy: offset.y }
    containerRef.current?.setPointerCapture(e.pointerId)
  }

  function onPointerMove(e) {
    if (!dragging.current) return
    const dx = e.clientX - dragStart.current.mx
    const dy = e.clientY - dragStart.current.my
    setOffset(clamp(dragStart.current.ox + dx, dragStart.current.oy + dy, dw, dh))
  }

  function onPointerUp() {
    dragging.current = false
  }

  function onZoom(e) {
    const z = Number(e.target.value)
    setZoom(z)
    setOffset((prev) => clamp(prev.x, prev.y, natural.w * fitScale * z, natural.h * fitScale * z))
  }

  function handleCrop() {
    const OUT = 400
    const canvas = document.createElement('canvas')
    canvas.width = OUT
    canvas.height = OUT
    const ctx = canvas.getContext('2d')

    // Image position in container space
    const imgLeft = (CONTAINER - dw) / 2 + offset.x
    const imgTop = (CONTAINER - dh) / 2 + offset.y

    // Crop circle center → natural image coordinates
    const toNat = natural.w / dw
    const cropCx = CONTAINER / 2 - imgLeft
    const cropCy = CONTAINER / 2 - imgTop
    const cropR = CROP_D / 2

    // Draw square crop — CSS (rounded-full / rounded-2xl) handles the shape display
    ctx.drawImage(
      imgRef.current,
      (cropCx - cropR) * toNat,
      (cropCy - cropR) * toNat,
      CROP_D * toNat,
      CROP_D * toNat,
      0, 0, OUT, OUT,
    )
    canvas.toBlob((blob) => onCrop(blob), 'image/jpeg', 0.92)
  }

  const imgLeft = (CONTAINER - dw) / 2 + offset.x
  const imgTop = (CONTAINER - dh) / 2 + offset.y

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />

      {/* Card */}
      <motion.div
        className="relative bg-surface border border-border rounded-2xl w-[380px] overflow-hidden shadow-2xl"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', duration: 0.35, bounce: 0.12 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="text-sm font-semibold text-text">Crop photo</p>
          <button
            onClick={onCancel}
            className="w-6 h-6 rounded-md flex items-center justify-center text-text-muted hover:text-text hover:bg-surface-raised transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Crop viewport */}
        <div className="px-4 pt-4 pb-3">
          <div
            ref={containerRef}
            className="relative rounded-xl overflow-hidden select-none bg-black"
            style={{
              width: CONTAINER,
              height: CONTAINER,
              margin: '0 auto',
              cursor: loaded ? 'grab' : 'default',
            }}
            onPointerDown={loaded ? onPointerDown : undefined}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {/* Image */}
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              onLoad={(e) => {
                setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })
                setLoaded(true)
              }}
              className="absolute pointer-events-none"
              style={{
                width: dw,
                height: dh,
                left: imgLeft,
                top: imgTop,
                userSelect: 'none',
              }}
            />

            {/* Dark overlay outside crop circle — box-shadow trick, clipped by overflow:hidden */}
            {loaded && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: CROP_D,
                  height: CROP_D,
                  left: (CONTAINER - CROP_D) / 2,
                  top: (CONTAINER - CROP_D) / 2,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)',
                  border: '1.5px solid rgba(255,255,255,0.55)',
                }}
              />
            )}

            {/* Rule-of-thirds grid inside crop circle */}
            {loaded && (
              <svg
                className="absolute pointer-events-none"
                style={{
                  width: CROP_D,
                  height: CROP_D,
                  left: (CONTAINER - CROP_D) / 2,
                  top: (CONTAINER - CROP_D) / 2,
                  overflow: 'hidden',
                }}
              >
                <clipPath id="circle-clip">
                  <circle cx={CROP_D / 2} cy={CROP_D / 2} r={CROP_D / 2} />
                </clipPath>
                <g clipPath="url(#circle-clip)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5">
                  <line x1={CROP_D / 3} y1="0" x2={CROP_D / 3} y2={CROP_D} />
                  <line x1={(CROP_D * 2) / 3} y1="0" x2={(CROP_D * 2) / 3} y2={CROP_D} />
                  <line x1="0" y1={CROP_D / 3} x2={CROP_D} y2={CROP_D / 3} />
                  <line x1="0" y1={(CROP_D * 2) / 3} x2={CROP_D} y2={(CROP_D * 2) / 3} />
                </g>
              </svg>
            )}

            {/* Loading spinner */}
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Zoom slider */}
          {loaded && (
            <div className="flex items-center gap-3 mt-3">
              <span className="text-[11px] text-text-faint select-none">−</span>
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={onZoom}
                className="flex-1 cursor-pointer"
                style={{ accentColor: 'var(--color-accent)' }}
              />
              <span className="text-[11px] text-text-faint select-none">+</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={handleCrop} disabled={!loaded}>
            <Check size={13} className="mr-1" />
            Crop photo
          </Button>
        </div>
      </motion.div>
    </motion.div>
  )
}
